import React, { createContext, useContext, useEffect, useState } from 'react';
import * as api from '../services/api';

const DevelopmentEnginesContext = createContext();

export const defaultDevelopmentEngines = {
    sectionTitle: 'Nossos Motores de Desenvolvimento',
    engines: [
        {
            id: 'engine-systems',
            number: '01',
            title: 'Sistemas & Apps',
            description: 'Projetamos e evoluímos plataformas digitais de alta performance, seguras e preparadas para escala. Entregamos experiências que combinam eficiência operacional, usabilidade e impacto real no resultado do negócio.',
            capabilities: ['Arquiteturas Cloud-Native', 'Apps Multiplataforma', 'Interfaces de Alto Impacto']
        },
        {
            id: 'engine-data',
            number: '02',
            title: 'Dados & IA',
            description: 'Convertemos dados dispersos em inteligência acionável. Com engenharia de dados robusta e IA aplicada, estruturamos decisões com mais precisão, velocidade e vantagem competitiva sustentável.',
            capabilities: ['Engenharia de Dados (ETL)', 'Suporte à Decisão', 'Inteligência Artificial']
        },
        {
            id: 'engine-cloud',
            number: '03',
            title: 'Cloud & DevOps',
            description: 'Estruturamos ambientes cloud resilientes, observáveis e prontos para crescimento contínuo. Com práticas DevOps, aceleramos entregas com governança, estabilidade e segurança de nível corporativo.',
            capabilities: ['Modernização Serverless', 'Automação de Infra (IaC)', 'Arquitetura DevOps']
        },
        {
            id: 'engine-innovation',
            number: '04',
            title: 'Inovação e P&D',
            description: 'Conduzimos ciclos de pesquisa aplicada e desenvolvimento tecnológico para validar hipóteses, reduzir risco e acelerar inovação. Transformamos conhecimento técnico-científico em soluções concretas de alto valor.',
            capabilities: ['Framework de Inovação', 'R5 Labs & Research', 'Prototipagem MVP']
        }
    ]
};

const sanitizeEnginesData = (raw) => {
    const base = defaultDevelopmentEngines;
    const byIndex = Array.isArray(raw?.engines) ? raw.engines : [];

    const mergedEngines = base.engines.map((fallback, idx) => {
        const fromRaw =
            byIndex.find((item) => item?.id === fallback.id) ||
            byIndex.find((item) => item?.number === fallback.number) ||
            byIndex[idx] ||
            {};

        const capabilities = Array.isArray(fromRaw.capabilities) ? fromRaw.capabilities : [];
        const mergedCapabilities = fallback.capabilities.map((defaultCap, capIdx) => {
            const candidate = (capabilities[capIdx] || '').toString().trim();
            return candidate || defaultCap;
        });

        return {
            id: fallback.id,
            number: fallback.number,
            title: (fromRaw.title || '').toString().trim() || fallback.title,
            description: (fromRaw.description || '').toString().trim() || fallback.description,
            capabilities: mergedCapabilities
        };
    });

    return {
        sectionTitle: (raw?.sectionTitle || '').toString().trim() || base.sectionTitle,
        engines: mergedEngines
    };
};

export const useDevelopmentEngines = () => {
    const context = useContext(DevelopmentEnginesContext);
    if (!context) {
        throw new Error('useDevelopmentEngines must be used within DevelopmentEnginesProvider');
    }
    return context;
};

export const DevelopmentEnginesProvider = ({ children }) => {
    const [enginesData, setEnginesData] = useState(defaultDevelopmentEngines);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDevelopmentEngines();
    }, []);

    const loadDevelopmentEngines = async () => {
        setLoading(true);
        try {
            const content = await api.getPageContent('development-engines');
            if (content) {
                setEnginesData(sanitizeEnginesData(content));
            } else {
                setEnginesData(defaultDevelopmentEngines);
            }
        } catch (error) {
            console.error('Error loading development engines:', error);
            setEnginesData(defaultDevelopmentEngines);
        } finally {
            setLoading(false);
        }
    };

    const updateDevelopmentEnginesData = async (newData) => {
        const sanitized = sanitizeEnginesData(newData);
        await api.savePageContent('development-engines', sanitized);
        setEnginesData(sanitized);
    };

    return (
        <DevelopmentEnginesContext.Provider
            value={{
                enginesData,
                loading,
                updateDevelopmentEnginesData,
                reloadDevelopmentEngines: loadDevelopmentEngines
            }}
        >
            {children}
        </DevelopmentEnginesContext.Provider>
    );
};
